import {
  $addUpdateTag,
  $getSelection,
  LexicalEditor,
  SKIP_DOM_SELECTION_TAG,
  SKIP_SELECTION_FOCUS_TAG,
  $createParagraphNode,
} from "lexical"
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list"
import { $patchStyleText, $isAtNodeEnd, $setBlocksType } from "@lexical/selection"

export const MIN_ALLOWED_FONT_SIZE = 8
export const MAX_ALLOWED_FONT_SIZE = 72
export const DEFAULT_FONT_SIZE = 15
const VERTICAL_GAP = 10
const HORIZONTAL_OFFSET = 5

const findFirstFocusableDescendant = (startElement: HTMLElement): HTMLElement | null => {
  const focusableSelector =
    "button, a[href], input, select, textarea, details, summary [tabindex], [contenteditable]"

  const focusableDescendants = startElement.querySelector(focusableSelector) as HTMLElement

  return focusableDescendants
}

export const isKeyboardInput = (event: MouseEvent | PointerEvent | React.MouseEvent): boolean => {
  if ("pointerId" in event && "pointerType" in event) {
    return event.pointerId === -1 && event.pointerType === ""
  }

  return event?.detail === 0
}

export const focusNearestDescendant = (startElement: HTMLElement): HTMLElement | null => {
  const el = findFirstFocusableDescendant(startElement)
  el?.focus()
  return el
}

export enum UpdateFontSizeType {
  increment = 1,
  decrement,
}

/**
 * Calculates the new font size based on the update type.
 * @param currentFontSize - The current font size
 * @param updateType - The type of change, either increment or decrement
 * @returns the next font size
 */
const calculateNextFontSize = (currentFontSize: number, updateType: UpdateFontSizeType | null) => {
  if (!updateType) {
    return currentFontSize
  }

  let updatedFontSize: number = currentFontSize
  switch (updateType) {
    case UpdateFontSizeType.decrement:
      switch (true) {
        case currentFontSize > MAX_ALLOWED_FONT_SIZE:
          updatedFontSize = MAX_ALLOWED_FONT_SIZE
          break
        case currentFontSize >= 48:
          updatedFontSize -= 12
          break
        case currentFontSize >= 24:
          updatedFontSize -= 4
          break
        case currentFontSize >= 14:
          updatedFontSize -= 2
          break
        case currentFontSize >= 9:
          updatedFontSize -= 1
          break
        default:
          updatedFontSize = MIN_ALLOWED_FONT_SIZE
          break
      }
      break

    case UpdateFontSizeType.increment:
      switch (true) {
        case currentFontSize < MIN_ALLOWED_FONT_SIZE:
          updatedFontSize = MIN_ALLOWED_FONT_SIZE
          break
        case currentFontSize < 12:
          updatedFontSize += 1
          break
        case currentFontSize < 20:
          updatedFontSize += 2
          break
        case currentFontSize < 36:
          updatedFontSize += 4
          break
        case currentFontSize <= 60:
          updatedFontSize += 12
          break
        default:
          updatedFontSize = MAX_ALLOWED_FONT_SIZE
          break
      }
      break

    default:
      break
  }
  return updatedFontSize
}

/**
 * Patches the selection with the updated font size.
 */
export const updateFontSizeInSelection = (
  editor: LexicalEditor,
  newFontSize: string | null,
  updateType: UpdateFontSizeType | null,
  skipRefocus: boolean
) => {
  const getNextFontSize = (prevFontSize: string | null): string => {
    if (!prevFontSize) {
      prevFontSize = `${DEFAULT_FONT_SIZE}px`
    }
    prevFontSize = prevFontSize.slice(0, -2)
    const nextFontSize = calculateNextFontSize(Number(prevFontSize), updateType)

    return `${nextFontSize}px`
  }

  editor.update(() => {
    if (skipRefocus) {
      $addUpdateTag(SKIP_DOM_SELECTION_TAG)
    }
    if (editor.isEditable()) {
      const selection = $getSelection()
      if (selection !== null) {
        $patchStyleText(selection, {
          "font-size": newFontSize || getNextFontSize,
        })
      }
    }
  })
}

export const getSelectedNode = (selection: any) => {
  const anchor = selection.anchor
  const focus = selection.focus
  const anchorNode = selection.anchor.getNode()
  const focusNode = selection.focus.getNode()
  if (anchorNode === focusNode) {
    return anchorNode
  }
  const isBackward = selection.isBackward()
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode
  }
}

export const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    $addUpdateTag(SKIP_SELECTION_FOCUS_TAG)
    const selection = $getSelection()
    $setBlocksType(selection, () => $createParagraphNode())
  })
}

export const formatOrderedList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "number") {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG)
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    })
  } else {
    formatParagraph(editor)
  }
}

export const formatBulletList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "bullet") {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG)
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    })
  } else {
    formatParagraph(editor)
  }
}

export const formatCheckList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "check") {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG)
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
    })
  } else {
    formatParagraph(editor)
  }
}

export const setFloatingElemPositionForLinkEditor = (
  targetRect: DOMRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET
): void => {
  const scrollerElem = anchorElem.parentElement

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = "0"
    floatingElem.style.transform = "translate(-10000px, -10000px)"
    return
  }

  const floatingElemRect = floatingElem.getBoundingClientRect()
  const anchorElementRect = anchorElem.getBoundingClientRect()
  const editorScrollerRect = scrollerElem.getBoundingClientRect()

  let top = targetRect.top - verticalGap
  let left = targetRect.left - horizontalOffset

  if (top < editorScrollerRect.top) {
    top += floatingElemRect.height + targetRect.height + verticalGap * 2
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset
  }

  top -= anchorElementRect.top
  left -= anchorElementRect.left

  floatingElem.style.opacity = "1"
  floatingElem.style.transform = `translate(${left}px, ${top}px)`
}

const SUPPORTED_URL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "sms:", "tel:"])

export const sanitizeUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return "about:blank"
    }
  } catch {
    return url
  }
  return url
}
